import app from '$$app';
import toast from '$$vueCmp/toast';
import axios from 'axios';

const internalApi = axios.create({
  baseURL: '/api/internal',
});

const api = axios.create({
  baseURL: '/api',
});

const socketInterceptor = req => {
  const methods = ['post', 'put', 'delete'];

  if (methods.includes(req.method)) {
    req.headers.socketId = app.socket?.id || '';
  }

  return req;
};

const metaInterceptor = req => {
  req.meta = req.meta || {};
  req.meta.requestStartedAt = new Date().getTime();

  if (req.data) {
    req.meta.data = req.data;
  }

  return req;
};

api.interceptors.request.use(metaInterceptor);
internalApi.interceptors.request.use(metaInterceptor);

api.interceptors.request.use(socketInterceptor);
internalApi.interceptors.request.use(socketInterceptor);

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-csrf-token']);

function getUrlPath(rawUrl) {
  if (typeof rawUrl !== 'string') return 'unknown';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return new URL(rawUrl).pathname;
  return rawUrl.split('?')[0];
}

// Trace headers useful for correlating with server logs
const TRACE_RESPONSE_HEADERS = ['cf-ray', 'x-request-id', 'x-trace-id', 'x-amz-request-id', 'server-timing'];

function extractRequestHeaders(config) {
  const method = config.method?.toLowerCase();
  const raw = config.headers || {};
  const merged = {};

  for (const source of [raw.common, raw[method], raw]) {
    if (!source || typeof source !== 'object') continue;
    for (const [key, val] of Object.entries(source)) {
      if (typeof val === 'object' && val !== null) continue;
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) continue;
      merged[key] = val;
    }
  }

  return Object.keys(merged).length ? merged : undefined;
}

function extractTraceHeaders(response) {
  const headers = {};
  for (const key of TRACE_RESPONSE_HEADERS) {
    const val = response.headers?.[key];
    if (val != null) headers[key] = val;
  }
  return Object.keys(headers).length ? headers : undefined;
}

function buildAxiosErrorMeta(error, scope) {
  let requestBody;
  if (error.config?.data) {
    try {
      requestBody = typeof error.config.data === 'string' ? JSON.parse(error.config.data) : error.config.data;
    } catch {
      requestBody = undefined;
    }
  }

  const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
  const apiUrl = (error.config?.baseURL || '') + (error.config?.url || '');
  const urlPath = getUrlPath(error.config?.url);
  const status = error.response?.status;
  const statusPart = status ? ` (${status})` : '';
  const durationMs = error.config?.meta?.requestStartedAt
    ? Date.now() - error.config.meta.requestStartedAt
    : undefined;
  const xhr = error.request;
  const errorName = status ? `STORE_API_ERROR_${status}` : 'STORE_API_NETWORK_ERROR';

  // Captures app-level stack from our interceptor code, not axios internals
  const capturedError = new Error(`[${scope}] ${method} ${urlPath} failed${statusPart} — ${error.message}`);

  const meta = {
    apiRequest: {
      scope,
      method,
      url: apiUrl,
      urlPath,
      params: error.config?.params,
      headers: extractRequestHeaders(error.config),
      body: requestBody,
      timeout: error.config?.timeout,
      withCredentials: error.config?.withCredentials,
      axiosCode: error.code,
      durationMs,
    },
  };

  if (error.response) {
    meta.apiResponse = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      traceHeaders: extractTraceHeaders(error.response),
    };
  }

  if (xhr) {
    // readyState: 0=UNSENT 1=OPENED 2=HEADERS_RECEIVED 3=LOADING 4=DONE
    // status=0 with readyState=4 → network error (offline, CORS, firewall, VPN blocking)
    meta.xhr = {
      status: xhr.status,
      readyState: xhr.readyState,
      responseURL: xhr.responseURL,
      withCredentials: xhr.withCredentials,
    };
  }

  // Network conditions — only useful for no-response errors (offline, VPN, firewall, DNS)
  if (!error.response) {
    const conn = typeof navigator !== 'undefined' ? navigator.connection : undefined;
    meta.network = {
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      type: conn?.type,
      effectiveType: conn?.effectiveType,
      downlinkMbps: conn?.downlink,
      rttMs: conn?.rtt,
      saveData: conn?.saveData,
    };
  }

  return { meta, capturedError, errorName };
}

internalApi.interceptors.response.use(response => response,
  error => {
    if (error.config.url.includes('/ganttview')) {
      const options = error.response?.status === 502 || error.message === 'Network Error' || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? { timeout: false } : {};

      toast.error(__('error_tasks_api_operation'), options);
    }
    if (error.config.url.includes('/resources')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/custom_columns')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/comments')) {
      toast.error(__('error_tasks_api_operation'));
    }

    if (error.config.url.includes('/attachment')) {
      if (error.response?.data.code === 4005) {
        toast.error(__('fileUpload_limitStore', { limit: '' }));
      } else {
        toast.error(__('error_tasks_api_operation'));
      }
    }

    error._ganttMeta = buildAxiosErrorMeta(error, 'internalApi');
    throw error;
  });

api.interceptors.response.use(response => response,
  error => {
    if (error.config.url.includes('/task')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/link')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/resources')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/custom_columns')) {
      toast.error(__('error_tasks_api_operation'));
    }
    if (error.config.url.includes('/history')) {
      toast.error(__('error_tasks_api_operation'));
    }

    error._ganttMeta = buildAxiosErrorMeta(error, 'api');
    throw error;
  });

// old webix support

const debouncedToastCall = _.debounce(locale => {
  toast.error(locale ? __(locale) : __('error_tasks_api_operation'));
}, 300);

webix.attachEvent('onBeforeAjax', (mode, url, data, request, headers, files, promise) => {
  // add code
  headers.socketId = app.socket.id || '';
  promise.then(data => {
    // try {
    //   data.json();
    // } catch (e) {
    //   debauncedToastCall();
    // }
  }, error => {
    debouncedToastCall();
  });
});

export {
  api, internalApi
};

